import {get} from "lodash"
import BaseService from "./Base"
import {
    CreateStripePaymentIntentApiPayload,
    CreateAuthorizeNetStudentPaymentApiPayload,
    CompleteAuthorizeNetStudentPaymentApiPayload,
    StudentPayment,
    CurrencyExchangeRates,
    StudentPaymentCharge,
    CreateSquarePaymentApiPayload,
    CreateDirectEmbedStudentPaymentApiPayload,
    MakeDirectEmbedStudentPaymentApiPayload,
    RecurringPaymentInterval,
    StudentPaymentMethod,
    StudentRecurringPayment,
    CreateRecurringPaymentPayload,
    TokenizeCardDirectEmbedStudentPaymentAPIPayload,
    CapturePaypalOrderPaymentApiPayload,
    CreatePapalOrderApiPayload,
    RefundStudentPaymentPayload
} from "types/student-account/student-payment"
import {DeepPartial} from "types/common"
import {StudentCharge} from "types/student-account/student-ledger"

export default class StudentPaymentService extends BaseService {
    async getPaymentProviderConfigs(params: {profileId?: number}) {
        const response = await this.get("/v1/students/payments/providers/config", params)
        return get(response, "data.data", [])
    }

    async getCurrencyExchangeRates(): Promise<CurrencyExchangeRates> {
        const response = await this.get("/v1/students/payments/currency-exchange-rates")
        return get(response, "data.data", {})
    }

    async getNinetyTenStats(params = {}): Promise<any> {
        const response = await this.post("/v1/students/payments/90-10-stats", params)
        return get(response, "data.data", {})
    }

    async getPayments(params = {}): Promise<{total: number; data: StudentPayment[]}> {
        const response = await this.post("/v1/students/payments/list", params)
        return get(response, "data", {})
    }

    createPayment = async (
        data: DeepPartial<StudentPayment> & {
            isSendInvoice?: boolean
        }
    ): Promise<StudentPayment> => {
        const response = await this.post("/v1/students/payments/create", data)
        return get(response, "data.data", {})
    }

    updatePayment = async (
        data: DeepPartial<StudentPayment> & {
            isSendInvoice?: boolean
        }
    ): Promise<StudentCharge> => {
        const response = await this.post("/v1/students/payments/edit", data)
        return get(response, "data.data", {})
    }

    refundPayment = async (payload: RefundStudentPaymentPayload): Promise<StudentPayment> => {
        const response = await this.post("/v1/students/payments/refund", payload)
        return get(response, "data.data", {})
    }

    assignPaymentCharges = async (
        paymentId: number,
        paymentCharges: Partial<StudentPaymentCharge>[]
    ): Promise<StudentPayment> => {
        const response = await this.post("/v1/students/payments/assign-charges", {paymentId, paymentCharges})
        return get(response, "data.data", {})
    }

    deletePayments = async (ids: number[]): Promise<void> => {
        await this.post("/v1/students/payments/delete", {ids})
    }

    async createPaymentIntent(
        data: CreateStripePaymentIntentApiPayload
    ): Promise<{clientSecret: string; requiresAction: boolean; studentPayment: StudentPayment}> {
        const response = await this.post("/v1/students/payments/stripe/create-payment-intent", data)
        return get(response, "data.data", {})
    }
    async createSquarePayment(data: CreateSquarePaymentApiPayload) {
        const response = await this.post("/v1/students/payments/square/create-payment", data)
        return get(response, "data.data", [])
    }

    async createAuthorizeNetStudentPayment(data: CreateAuthorizeNetStudentPaymentApiPayload) {
        const response = await this.post("/v1/students/payments/authorize-net/create", data)
        return get(response, "data.data", [])
    }

    async completeAuthorizeNetStudentPayment(data: CompleteAuthorizeNetStudentPaymentApiPayload) {
        const response = await this.post("/v1/students/payments/authorize-net/complete-student-payment", data)
        return get(response, "data.data", [])
    }

    async createDirectEmbedStudentPayment(data: CreateDirectEmbedStudentPaymentApiPayload) {
        const response = await this.post("/v1/students/payments/direct-embed/create", data)
        return get(response, "data.data", [])
    }

    async createPaypalOrder(data: CreatePapalOrderApiPayload) {
        const response = await this.post("/v1/students/payments/paypal/create-order", data)
        return get(response, "data.data", [])
    }

    async capturePaypalOrderPayment(data: CapturePaypalOrderPaymentApiPayload) {
        const response = await this.post("/v1/students/payments/paypal/capture-order", data)
        return get(response, "data.data", [])
    }

    async makeDirectEmbedStudentPayment(data: MakeDirectEmbedStudentPaymentApiPayload) {
        const response = await this.post("/v1/students/payments/direct-embed/make-payment", data)
        return get(response, "data.data", [])
    }

    //#region Payment Methods

    async getStudentPaymentMethods(profileId: number): Promise<StudentPaymentMethod[]> {
        const response = await this.post("/v1/students/payments/payment-methods/list", {profileId})
        return get(response, "data.data", [])
    }

    async createStudentPaymentMethod(payload: Partial<StudentPaymentMethod>): Promise<StudentPaymentMethod> {
        const response = await this.post("/v1/students/payments/payment-methods/create", payload)
        return get(response, "data.data", {})
    }

    async deleteStudentPaymentMethod(paymentMethodId: number): Promise<void> {
        await this.post("/v1/students/payments/payment-methods/delete", {paymentMethodId})
    }

    async setActiveStudentPaymentMethod(paymentMethodId: number): Promise<void> {
        await this.post("/v1/students/payments/payment-methods/set-active", {paymentMethodId})
    }

    retryStripePayment = async (
        paymentId: number,
        paymentMethodId: number
    ): Promise<{payment: StudentPayment; usedPaymentMethod: StudentPaymentMethod}> => {
        const response = await this.post("/v1/students/payments/stripe/retry", {paymentId, paymentMethodId})
        return get(response, "data.data", {})
    }

    //#endregion

    //#region recurring payments

    async getStudentRecurringPayments(profileId: number): Promise<StudentRecurringPayment[]> {
        const response = await this.post("/v1/students/payments/recurring-payments/list", {profileId})
        return get(response, "data.data", [])
    }

    async createStudentRecurringPayment(payload: CreateRecurringPaymentPayload): Promise<StudentRecurringPayment> {
        const response = await this.post("/v1/students/payments/recurring-payments/create", payload)
        return get(response, "data.data", {})
    }

    async cancelStudentRecurringPayment(recurringPaymentId: number): Promise<void> {
        await this.post("/v1/students/payments/recurring-payments/cancel", {recurringPaymentId})
    }

    async resumeStudentRecurringPayment(recurringPaymentId: number): Promise<void> {
        await this.post("/v1/students/payments/recurring-payments/resume", {recurringPaymentId})
    }

    async deleteStudentRecurringPayment(recurringPaymentId: number): Promise<void> {
        await this.post("/v1/students/payments/recurring-payments/delete", {recurringPaymentId})
    }

    async syncStudentRecurringPayment(recurringPaymentId: number): Promise<StudentRecurringPayment> {
        const response = await this.post("/v1/students/payments/recurring-payments/sync", {recurringPaymentId})
        return get(response, "data.data", {})
    }

    //#endregion

    //#region Stripe

    stripeCreateSetupIntent = async (profileId: number): Promise<string> => {
        const response = await this.post("/v1/students/payments/stripe/setup-intent/create", {profileId})
        return get(response, "data.data.clientSecret", "")
    }

    stripeAttachPaymentMethod = async (profileId: number, paymentMethodId: string): Promise<StudentPaymentMethod> => {
        const response = await this.post("/v1/students/payments/stripe/payment-methods/attach", {
            profileId,
            paymentMethodId
        })
        return get(response, "data.data", {})
    }

    //#endregion
}
